import type {
  TravelPlacePricingAndBookingDetailsFragment,
  TravelJoyPlacePricingAndBookingDetailsFragment,
} from '../../../apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/Accommodations.graphql.generated';
import type { TravelPlacePricingAndBookingDetailsPayload, AccommodationCatalogStayDatesInput, AccommodationCatalogUnitsOccupancyInput } from '@graphql/generated-base';
import type { TravelPlacesPricingOverrides } from './useTravelPlacesPricingAndBookingDetails.types';
import {
  TravelPlacePricingAndBookingDetailsFragmentDoc,
  useTravelPlacesPricingAndBookingDetailsQuery,
  useTravelPlacesPricingAndBookingDetailsLazyQuery,
  TravelJoyPlacePricingAndBookingDetailsFragmentDoc,
} from '../../../apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/Accommodations.graphql.generated';
import { TravelCurrencyCode, DateResolutionStrategy } from '@graphql/generated-base';
import type { ApolloClient, ApolloError } from '@apollo/client';
import { makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCheckInCheckoutDates } from '../useCheckInCheckoutDates';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';
import { getCalculatedPrice } from '@shared/utils/accommodationPrice';
import { useMinimumPercentageOffToDisplay } from '../useMinimumPercentageOffToDisplay';
import { useSearchAccommodationsQueryMetadataPayload } from '../useSearchAccommodationsQueryMetadataPayload';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { hasAnyCancellationPolicyFree } from '@shared/utils/accommodations/AccommodationTiles';
import { readNavigatorLanguage } from '../../utils/getNavigatorLanguage';
import { isBotClient } from '../../utils/botDetection/isBotClient';

export const queryStatusVar = makeVar<{
  loading: boolean;
  called: boolean;
  error?: ApolloError;
}>({ loading: false, called: false, error: undefined });

export const minStayRetryVar = makeVar<{
  extendedIds: ReadonlySet<string>;
  extendedNights: number | null;
}>({ extendedIds: new Set(), extendedNights: null });

/**
 *
 * @param travelPlaceIds - array of travelPlaceIds to fetch pricing
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 * @param skipOnInitialLoad - Skip the query on initial load
 * @param overrides - Override default dates and occupancy
 * @param skipIfNoSearchParams - Skip if search params are not available
 * @param eventCurrency - Optional event-level currency from TravelSettings
 */

export const useTravelPlacesPricingAndBookingDetails = (
  travelPlaceIds: string[],
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace',
  skipOnInitialLoad = false,
  overrides: TravelPlacesPricingOverrides = {},
  skipIfNoSearchParams = false,
  eventCurrency?: TravelCurrencyCode | null,
  { updateQueryStatus = true }: { updateQueryStatus?: boolean } = {},
) => {
  const { eventInfo, loading: isLoadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const derivedDates = useCheckInCheckoutDates({ eventDate });

  const checkInDate = overrides.dates?.checkIn || derivedDates.checkIn;
  const checkOutDate = overrides.dates?.checkOut || derivedDates.checkOut;

  const { currency: currencyCode, loading: loadingTravelCurrencyAllowlist } = useTravelWhiteLabelCurrency(eventCurrency);
  const { isPreparingSearchMetadataPayload, searchMetadata } = useSearchAccommodationsQueryMetadataPayload();

  const payload: TravelPlacePricingAndBookingDetailsPayload | undefined = useMemo(() => {
    if (loadingTravelCurrencyAllowlist || isPreparingSearchMetadataPayload || !eventInfo?.eventId) {
      return undefined;
    }

    return {
      currencyCode: (currencyCode || TravelCurrencyCode.USD) as TravelCurrencyCode,
      dates: {
        checkIn: {
          // validated with skip
          high: checkInDate,
          low: checkInDate,
        },
        // validated with skip
        checkOut: {
          high: checkOutDate,
          low: checkOutDate,
        },
      },
      occupancies: overrides.occupancies || {
        occupancies: [
          {
            adults: 2,
            childrenAges: [],
          },
        ],
        unitCount: 1,
      },
      metadata: searchMetadata,
      ...(travelPlaceIdType === 'accommodationPlace' && { accommodationPlaceIds: travelPlaceIds }),
      ...(travelPlaceIdType === 'joyPlace' && { joyPlaceIds: travelPlaceIds }),
      dateResolutionStrategy: overrides.dateResolutionStrategy ?? DateResolutionStrategy.USE_EVENT_DATES,
    };
  }, [
    loadingTravelCurrencyAllowlist,
    isPreparingSearchMetadataPayload,
    eventInfo?.eventId,
    currencyCode,
    checkInDate,
    checkOutDate,
    overrides.occupancies,
    overrides.dateResolutionStrategy,
    searchMetadata,
    travelPlaceIdType,
    travelPlaceIds,
  ]);

  const {
    data,
    loading: travelPlacesPricingAndBookingDetailsLoading,
    called,
    error,
  } = useTravelPlacesPricingAndBookingDetailsQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      payload: payload!,
    },
    skip: skipIfNoSearchParams || skipOnInitialLoad || !payload || !travelPlaceIds.length || isBotClient(),
    onError(error) {
      addError(error, {
        source: 'Pricing api failed',
        context: 'travelPlacesPricingAndBookingDetails',
        errorMessage: error.message,
        checkInDate,
        checkOutDate,
        currencyCode: payload?.currencyCode,
        metadata: payload?.metadata,
        accommodationPlaceIds: payload?.accommodationPlaceIds,
        joyPlaceIds: payload?.joyPlaceIds,
      });
    },
    onCompleted(data) {
      const accommodationPlaces = data?.travelPlacesPricingAndBookingDetails?.accommodationPlaces;
      if (!accommodationPlaces?.length) {
        addAction('Pricing api empty results', {
          context: 'travelPlacesPricingAndBookingDetails',
          checkInDate,
          checkOutDate,
          currencyCode: payload?.currencyCode,
          metadata: payload?.metadata,
          accommodationPlaceIds: payload?.accommodationPlaceIds,
          joyPlaceIds: payload?.joyPlaceIds,
        });
      }
    },
  });

  const [
    getTravelPlacesPricingAndBookingDetails,
    {
      data: lazyData,
      loading: loadingGetTravelPlacesPricingAndBookingDetails,
      called: calledGetTravelPlacesPricingAndBookingDetails,
      error: errorGetTravelPlacesPricingAndBookingDetails,
    },
  ] = useTravelPlacesPricingAndBookingDetailsLazyQuery({
    batchMode: 'fast',
    onError: error => {
      addError(error, {
        context: 'useTravelPlacesPricingAndBookingDetailsQuery',
      });
    },
  });

  const fetchTravelPricingData = useCallback(
    (travelIdsToFetch: string[]) => {
      if (!travelIdsToFetch.length) {
        return;
      }

      getTravelPlacesPricingAndBookingDetails({
        variables: {
          payload: {
            ...payload,
            currencyCode: payload?.currencyCode as TravelCurrencyCode,
            dates: payload?.dates as AccommodationCatalogStayDatesInput,
            occupancies: payload?.occupancies as AccommodationCatalogUnitsOccupancyInput,
            ...(travelPlaceIdType === 'accommodationPlace' && { accommodationPlaceIds: travelIdsToFetch }),
            ...(travelPlaceIdType === 'joyPlace' && { joyPlaceIds: travelIdsToFetch }),
          }!,
        },
      });
    },
    [payload, travelPlaceIdType, getTravelPlacesPricingAndBookingDetails],
  );

  const isLoading =
    travelPlacesPricingAndBookingDetailsLoading ||
    loadingTravelCurrencyAllowlist ||
    isPreparingSearchMetadataPayload ||
    isLoadingEventInfo ||
    loadingGetTravelPlacesPricingAndBookingDetails;

  // When the query is intentionally skipped (bots, no place IDs, etc.) and all
  // loading prerequisites have settled, treat the pricing system as "called" so
  // fragment consumers don't show an infinite skeleton.
  const isQuerySkipped = !called && !isLoading && (skipIfNoSearchParams || skipOnInitialLoad || !payload || !travelPlaceIds.length || isBotClient());

  useEffect(() => {
    if (updateQueryStatus) {
      queryStatusVar({
        loading: isLoading,
        called: called || calledGetTravelPlacesPricingAndBookingDetails || isQuerySkipped,
        error: error || errorGetTravelPlacesPricingAndBookingDetails,
      });
    }
  }, [called, calledGetTravelPlacesPricingAndBookingDetails, error, errorGetTravelPlacesPricingAndBookingDetails, isLoading, isQuerySkipped, updateQueryStatus]);

  return {
    data: data || lazyData,
    loading: isLoading,
    error: error || errorGetTravelPlacesPricingAndBookingDetails,
    checkInDate,
    checkOutDate,
    fetchTravelPricingData,
    payload,
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, travelPlaceId: string, travelPlaceIdType: 'accommodationPlace' | 'joyPlace') => {
  // Define the fragment
  const fragment = TravelPlacePricingAndBookingDetailsFragmentDoc;
  const joyPlaceFragment = TravelJoyPlacePricingAndBookingDetailsFragmentDoc;

  // Generate the cache ID based on the type and key fields
  let cacheId = `AccommodationPlacePricingAndBookingDetails:{"accommodationPlace":{"id":"${travelPlaceId}"}}`;
  if (travelPlaceIdType === 'joyPlace') {
    cacheId = `JoyPlacePricingAndBookingDetails:{"joyPlace":{"id":"${travelPlaceId}"}}`;
  }

  // Read the fragment from the cache
  const data = client.cache.readFragment<TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment>({
    id: cacheId,
    fragment: travelPlaceIdType === 'joyPlace' ? joyPlaceFragment : fragment,
  });

  return data;
};

type HookReturnType = Readonly<{
  price: Maybe<string>;
  priceInMinorUnits: Maybe<number>;
  strikeOutPrice: Maybe<string>;
  percentageOff: Maybe<number>;
  totalAmount: Maybe<string>;
  originalPrice: Maybe<string>;
  data: Maybe<TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment>;
  loading: boolean;
  /** Whether the pricing query has been executed at least once. */
  called: boolean;
  soldOutUrl?: string;
  /** True when at least one cancellation window on the "from" rate has a zero fee. */
  hasFreeCancellation: boolean;
  error?: Error;
}>;

const defaultReturn: HookReturnType = {
  price: undefined,
  priceInMinorUnits: undefined,
  strikeOutPrice: undefined,
  percentageOff: undefined,
  totalAmount: undefined,
  originalPrice: undefined,
  data: undefined,
  loading: true,
  called: false,
  hasFreeCancellation: false,
} as const;

/**
 *
 * @param travelPlaceId - accommodationPlaceId to fetch pricing for
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 * @returns
 */
export const useTravelPlacesPricingAndBookingDetailsFragment = (
  travelPlaceId: Maybe<string>,
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace',
  skip?: boolean,
): HookReturnType => {
  const client = useApolloClient();
  const navigatorLanguage = readNavigatorLanguage();
  const { loading, called, error } = useReactiveVar(queryStatusVar);
  // Subscribe to minStayRetryVar so all fragment consumers re-read the cache
  // when the extended-dates retry updates pricing for previously sold-out hotels.
  useReactiveVar(minStayRetryVar);
  const { minimumPercentageOffToDisplayValue, loadingMinimumPercentageOffToDisplay } = useMinimumPercentageOffToDisplay();

  // Skip check must come first — callers with pre-supplied data pass skip=true
  // and should not be blocked by the global pricing query's lifecycle.
  if (skip) {
    return {
      ...defaultReturn,
      loading: false,
      called,
    };
  }
  if (loading || !called || loadingMinimumPercentageOffToDisplay) {
    return {
      ...defaultReturn,
      called,
      loading: true,
    };
  }
  if (error) {
    return {
      ...defaultReturn,
      price: null,
      priceInMinorUnits: null,
      loading: false,
      called: true,
      error: error,
    };
  }
  const data = readFragmentFromCache(client, travelPlaceId!, travelPlaceIdType);

  const { price, strikeOutPrice, percentageOff, totalAmount, minorUnits, originalPrice } = getCalculatedPrice(
    data,
    navigatorLanguage,
    minimumPercentageOffToDisplayValue,
    travelPlaceId,
  );

  const soldOutUrl = extractHotelRedirects(data?.redirectMechanisms)?.hotelSearchPageUrl?.url;
  // Only the accommodationPlace fragment selects cancellationPolicies.
  const hasFreeCancellation = data && 'accommodationPlace' in data ? hasAnyCancellationPolicyFree(data.pricingInfo?.cancellationPolicies) : false;

  return { price, priceInMinorUnits: minorUnits, strikeOutPrice, percentageOff, totalAmount, data, loading: loading, called: true, soldOutUrl, originalPrice, hasFreeCancellation };
};

export const getTravelPlacesPricingAndBookingDetailsFragments = <T>(
  client: ApolloClient<object>,
  travelPlaceIds: ReadonlyArray<Maybe<string>>,
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace',
): T[] => {
  const results: T[] = [];

  travelPlaceIds.forEach(travelPlaceId => {
    if (!travelPlaceId) return;
    const data = readFragmentFromCache(client, travelPlaceId!, travelPlaceIdType);
    if (data) results.push(data as T);
  });

  return results;
};
